import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor, RefreshCw, Calendar, FileSpreadsheet, Settings as SettingsIcon, LogOut, Archive as ArchiveIcon, BarChart3, Users, UserPlus, UserCheck, Sparkles, Download, BookOpen } from "lucide-react";
import api, { clearSession, getToken, getLevel, setSession } from "@/lib/api";
import { toast } from "sonner";
import LiveClock from "@/components/LiveClock";
import SyncIndicator from "@/components/SyncIndicator";
import SummaryCard from "@/components/SummaryCard";
import AttendanceTable from "@/components/AttendanceTable";
import UnmatchedPanel from "@/components/UnmatchedPanel";

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [filterPin, setFilterPin] = useState(null);
  // adminPrompt modal removed — non-admin users no longer see the Settings button
  // and therefore have no entry point that needs the admin-passcode prompt.
  const [exportDate, setExportDate] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => { if (!getToken()) navigate("/"); }, [navigate]);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/crew-dashboard/today");
      setData(res.data);
      // Prefer the CampMinder roster sync timestamp (that's the relevant
      // freshness check). Fall back to the legacy visits-poll key if the
      // CampMinder one isn't present (very old configs).
      const cfg = res.data?.config || {};
      setLastSyncAt(cfg.campminder_last_sync_at_utc || cfg.last_visits_poll_at || null);
      setError("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to load attendance";
      setError(typeof msg === "string" ? msg : "Failed to load attendance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  // Auto-lock after 30 min idle
  useEffect(() => {
    let idle = setTimeout(logout, 30 * 60 * 1000);
    const reset = () => { clearTimeout(idle); idle = setTimeout(logout, 30 * 60 * 1000); };
    ["mousemove", "keydown", "touchstart"].forEach((e) => window.addEventListener(e, reset));
    return () => {
      clearTimeout(idle);
      ["mousemove", "keydown", "touchstart"].forEach((e) => window.removeEventListener(e, reset));
    };
  }, []);

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      await api.post("/sync/visits");
      await load();
      toast.success("Attendance refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const exportFile = async (kind, dateOverride) => {
    const date = dateOverride || data?.date;
    if (!date) return;
    try {
      const res = await api.get(`/export/${kind}`, { params: { date }, responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rrdc-attendance-${date}.${kind === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const [plannedAbsBusy, setPlannedAbsBusy] = useState(false);
  const [plannedLateBusy, setPlannedLateBusy] = useState(false);
  // Helper that downloads either grid (absences or latenesses) — same shape,
  // different endpoint. The 2nd arg lets the toast say the right thing.
  const downloadPlannedXlsx = (endpointBase, setterBusy, label) => async () => {
    setterBusy(true);
    try {
      const res = await api.get(`${endpointBase}/xlsx`, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const disp = res.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(disp);
      const fname = match ? match[1] : `rrdc-${label.toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0,10)}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} download failed`);
    } finally {
      setterBusy(false);
    }
  };
  const downloadPlannedAbsences = downloadPlannedXlsx("/reports/planned-absences-grid", setPlannedAbsBusy, "Planned Absences");
  const downloadPlannedLatenesses = downloadPlannedXlsx("/reports/planned-latenesses-grid", setPlannedLateBusy, "Planned Lateness");

  // New-tab hand-off helper. sessionStorage is per-tab so we pass the
  // current token+level via URL hash; `Protected` hydrates and strips it.
  const openReportInNewTab = (path) => {
    const tok = getToken() || "";
    const lvl = getLevel() || "admin";
    const url = `${path}#t=${encodeURIComponent(tok)}&l=${encodeURIComponent(lvl)}`;
    window.open(url, "_blank", "noopener");
  };

  const logout = () => { clearSession(); navigate("/"); };

  // ---- Summer Payroll download (leadership only) ----
  const [payrollDownloading, setPayrollDownloading] = useState(false);
  const downloadPayroll = async () => {
    setPayrollDownloading(true);
    try {
      const res = await api.get("/admin/payroll/export.xlsx", { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rolling-River-Summer-Payroll-${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e?.response?.status === 403
        ? "Leadership access required."
        : (e?.response?.data?.detail || "Download failed");
      // eslint-disable-next-line no-alert
      alert(msg);
    } finally { setPayrollDownloading(false); }
  };

  // ---- Master Attendance download (leadership only) ----
  const [masterDownloading, setMasterDownloading] = useState(false);
  const downloadMasterAttendance = async () => {
    setMasterDownloading(true);
    try {
      const res = await api.get("/admin/reports/master-attendance.xlsx", { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rolling-River-Master-Attendance-${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e?.response?.status === 403
        ? "Leadership access required."
        : (e?.response?.data?.detail || "Download failed");
      // eslint-disable-next-line no-alert
      alert(msg);
    } finally { setMasterDownloading(false); }
  };

  const goSettings = () => {
    const lvl = getLevel();
    if (lvl === "admin" || lvl === "leadership") return navigate("/settings");
    // Non-admin/leadership should never see the Settings entry points;
    // belt-and-suspenders no-op if they somehow reach this handler.
  };

  // adminPrompt modal removed — non-admin users no longer see the Settings button
  // and therefore have no entry point that needs the admin-passcode prompt.

  const summary = data?.summary || {};
  const records = data?.records || [];
  const filtered = filterPin ? records.filter((r) => r.status === filterPin) : records;

  const setupNeeded = false;
  const cfg = data?.config;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1500px] mx-auto px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src="/logo.png" alt="Rolling River Day Camp" className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl object-contain bg-white shadow-sm p-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.2em] text-slate-500 font-bold truncate">Rolling River Day Camp</div>
              <h1 className="font-display text-base sm:text-lg font-bold text-slate-900 leading-tight truncate">Staff Snapshot</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <div className="hidden md:block"><SyncIndicator lastSyncAtUtc={lastSyncAt} /></div>
            <div className="hidden sm:block"><LiveClock /></div>
            <button onClick={logout} className="btn-touch inline-flex items-center gap-1 px-2 sm:px-3 rounded-lg text-sm text-slate-600 hover:text-red-700 hover:bg-red-50" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Lock</span>
            </button>
          </div>
        </div>
        {/* operating banner */}
        <div className="border-t border-slate-100 bg-blue-50/60">
          <div className="max-w-[1500px] mx-auto px-3 sm:px-6 py-2 text-xs text-slate-600 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center flex-wrap gap-x-1 gap-y-0.5">
              <span className="font-semibold text-slate-700">Hours:</span>
              <span className="tabular-nums">{cfg?.operating_start_local || "08:20"}–{cfg?.operating_end_local || "16:10"}</span>
              <span className="mx-1 sm:mx-2 text-slate-300">|</span>
              <span className="font-semibold text-slate-700">Late</span>
              <span className="tabular-nums">{cfg?.late_threshold_local || "08:30"}</span>
              <span className="mx-1 sm:mx-2 text-slate-300">|</span>
              <span className="font-semibold text-slate-700">Absent</span>
              <span className="tabular-nums">{cfg?.absent_threshold_local || "10:00"}</span>
            </div>
            {data?.is_final && (
              <div className="px-2.5 py-1 rounded-md bg-emerald-600 text-white font-bold text-[11px] tracking-[0.16em] uppercase" data-testid="final-badge">
                FINAL — Locked Record
              </div>
            )}
            <div className="md:hidden"><SyncIndicator lastSyncAtUtc={lastSyncAt} /></div>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
        {/* Quick actions — organized in 3 clear groups */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-3 mb-6" data-testid="dashboard-actions">
          {/* Live & navigation */}
          <div className="lg:col-span-5 rounded-2xl border border-slate-200 bg-white p-3 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold mr-1 hidden sm:inline">Live</span>
            <button
              onClick={refreshNow}
              disabled={refreshing}
              className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50"
              data-testid="refresh-now-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <button onClick={() => navigate("/staff-cards")} className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold" data-testid="staff-cards-btn">
              <Users className="w-4 h-4" /> Staff Cards
            </button>
            <button onClick={() => navigate("/admin/visitors")} className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold" data-testid="visitors-nav-btn">
              <UserPlus className="w-4 h-4" /> Visitors
            </button>
            <button onClick={() => navigate("/admin/staff-checkin")} className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-teal-100 hover:bg-teal-200 text-teal-900 font-semibold" data-testid="staff-checkin-nav-btn">
              <UserCheck className="w-4 h-4" /> Check-Ins
            </button>
            <button onClick={() => navigate("/calendar")} className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold" data-testid="calendar-btn">
              <Calendar className="w-4 h-4" /> Calendar
            </button>
          </div>

          {/* Reports & archive */}
          <div className="lg:col-span-4 rounded-2xl border border-slate-200 bg-white p-3 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold mr-1 hidden sm:inline">Reports</span>
            {(getLevel() === "admin" || getLevel() === "leadership") && (
              <>
                <button onClick={() => navigate("/reports")} className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold" data-testid="reports-btn">
                  <BarChart3 className="w-4 h-4" /> Reports
                </button>
                <button onClick={() => navigate("/archive")} className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold" data-testid="archive-btn">
                  <ArchiveIcon className="w-4 h-4" /> Archive
                </button>
                <button onClick={() => navigate("/orientation")} className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-indigo-100 hover:bg-indigo-200 text-indigo-900 font-semibold" data-testid="orientation-btn">
                  <Sparkles className="w-4 h-4" /> Orientation Games
                </button>
              </>
            )}
            <div className="inline-flex items-stretch rounded-xl bg-amber-100 hover:bg-amber-200 overflow-hidden" data-testid="planned-abs-group">
              <button
                onClick={() => openReportInNewTab("/reports/planned-absences")}
                className="btn-touch inline-flex items-center gap-2 px-3 text-amber-900 font-semibold"
                data-testid="planned-absences-view-btn"
                title="Open Planned Absences in a new tab"
              >
                <FileSpreadsheet className="w-4 h-4" /> Planned Absences
              </button>
              <button
                onClick={downloadPlannedAbsences}
                disabled={plannedAbsBusy}
                className="btn-touch inline-flex items-center gap-1 px-2 border-l border-amber-300 text-amber-900 font-semibold disabled:opacity-50"
                data-testid="planned-absences-download-btn"
                title="Download Excel directly"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
            <div className="inline-flex items-stretch rounded-xl bg-orange-100 hover:bg-orange-200 overflow-hidden" data-testid="planned-late-group">
              <button
                onClick={() => openReportInNewTab("/reports/planned-latenesses")}
                className="btn-touch inline-flex items-center gap-2 px-3 text-orange-900 font-semibold"
                data-testid="planned-latenesses-view-btn"
                title="Open Planned Lateness in a new tab"
              >
                <FileSpreadsheet className="w-4 h-4" /> Planned Lateness
              </button>
              <button
                onClick={downloadPlannedLatenesses}
                disabled={plannedLateBusy}
                className="btn-touch inline-flex items-center gap-1 px-2 border-l border-orange-300 text-orange-900 font-semibold disabled:opacity-50"
                data-testid="planned-latenesses-download-btn"
                title="Download Excel directly"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => navigate("/help")}
              className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-emerald-100 hover:bg-emerald-200 text-emerald-900 font-semibold"
              data-testid="help-btn"
              title="Open the SOP / Help section"
            >
              <BookOpen className="w-4 h-4" /> Help
            </button>
          </div>

          {/* Daily Excel + settings */}
          <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-3 flex items-center gap-2 flex-wrap justify-end">
            <div className="inline-flex items-stretch rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="archive-export-group">
              <input
                type="date"
                value={exportDate}
                onChange={(e) => setExportDate(e.target.value)}
                className="px-2 py-1.5 text-xs bg-white outline-none border-r border-slate-200 tabular-nums"
                data-testid="export-date-picker"
                max={data?.date}
              />
              <button onClick={() => exportFile("xlsx", exportDate)} className="btn-touch inline-flex items-center gap-1.5 px-3 hover:bg-slate-50 text-slate-700 font-semibold text-sm" data-testid="export-xlsx-btn">
                <FileSpreadsheet className="w-4 h-4" /> Excel
              </button>
            </div>
            {getLevel() === "leadership" && (
              <button
                onClick={downloadPayroll}
                disabled={payrollDownloading}
                className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl text-white font-semibold disabled:opacity-50"
                style={{ backgroundColor: "#3556A5" }}
                data-testid="download-payroll-btn"
                title="Generate the Rolling River Summer Payroll spreadsheet (leadership only)"
              >
                <FileSpreadsheet className="w-4 h-4" /> {payrollDownloading ? "Generating…" : "Download Summer Payroll"}
              </button>
            )}
            {getLevel() === "leadership" && (
              <button
                onClick={downloadMasterAttendance}
                disabled={masterDownloading}
                className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl text-white font-semibold disabled:opacity-50"
                style={{ backgroundColor: "#0F766E" }}
                data-testid="download-master-attendance-btn"
                title="Master attendance grid — one row per staff, one column per camp day (6/29–8/20). Leadership only."
              >
                <FileSpreadsheet className="w-4 h-4" /> {masterDownloading ? "Generating…" : "Download Master Attendance"}
              </button>
            )}
            {(getLevel() === "admin" || getLevel() === "leadership") && (
              <button onClick={goSettings} className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid="settings-btn">
                <SettingsIcon className="w-4 h-4" /> Settings
              </button>
            )}
          </div>
        </section>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="dashboard-error">
            {error}
          </div>
        )}

        {setupNeeded && (
          <div className="mb-4 px-5 py-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900" data-testid="setup-banner">
            <div className="font-semibold mb-1">Setup required</div>
            <div className="text-sm">
              No Approved List has been chosen yet.{" "}
              {(getLevel() === "admin" || getLevel() === "leadership") ? (
                <>Open <button onClick={goSettings} className="underline font-semibold">Settings</button> to pick the staff list.</>
              ) : (
                <>Ask an admin to sign in and pick the staff list in Settings.</>
              )}
            </div>
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-4">
          <SummaryCard kind="total_expected" value={summary.total_expected} onClick={() => setFilterPin(null)} active={filterPin === null} />
          <SummaryCard kind="present" value={summary.present} onClick={() => setFilterPin("Present")} active={filterPin === "Present"} />
          <SummaryCard kind="late" value={summary.late} onClick={() => setFilterPin("Late")} active={filterPin === "Late"} />
          <SummaryCard kind="late_arrival" value={summary.late_arrival} onClick={() => setFilterPin("Late Arrival")} active={filterPin === "Late Arrival"} />
          <SummaryCard kind="absent" value={summary.absent} onClick={() => setFilterPin("Absent")} active={filterPin === "Absent"} />
          <SummaryCard kind="signed_out" value={summary.signed_out} onClick={() => setFilterPin("Signed Out")} active={filterPin === "Signed Out"} />
        </div>

        {/* Active filter chip */}
        {filterPin && (
          <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-xs font-semibold" data-testid="filter-chip">
            Filtering: {filterPin}
            <button onClick={() => setFilterPin(null)} className="text-blue-600 hover:text-blue-900 font-bold" data-testid="clear-filter">×</button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-slate-500" data-testid="dashboard-loading">Loading attendance…</div>
        ) : (
          <>
            <AttendanceTable
              records={filtered}
              onPersonRefresh={refreshNow}
            />
            <UnmatchedPanel items={data?.unmatched || []} />
          </>
        )}
      </main>
    </div>
  );
}
